import { buttonVariants } from '@web-app/components/ui/Button/button';
import { Input } from '@web-app/components/ui/Input/input';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@web-app/components/ui/Tooltip/tooltip';
import { cn } from '@web-app/lib/utils';
import { useCallback, useState } from 'react';
import { LuX } from 'react-icons/lu';

import type { SessionGoalItemProps } from './types';

export const SessionGoalItem = ({
  goal,
  onArchive,
  onUpdate,
  isDisabled = false,
}: SessionGoalItemProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(goal.content);

  const handleEditClick = useCallback(() => {
    setEditContent(goal.content);
    setIsEditing(true);
  }, [goal.content]);

  const handleEditBlur = useCallback(() => {
    setIsEditing(false);
    const trimmed = editContent.trim();
    if (trimmed && trimmed !== goal.content && onUpdate) {
      onUpdate(trimmed);
    }
  }, [editContent, goal.content, onUpdate]);

  const handleEditKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.currentTarget.blur();
      } else if (e.key === 'Escape') {
        setEditContent(goal.content);
        setIsEditing(false);
      }
    },
    [goal.content],
  );

  return (
    <div
      data-testid={`session-goal-item-${goal.id}`}
      className="flex w-full items-center justify-between gap-3 p-2"
    >
      <div className="flex min-w-0 flex-1 items-center gap-2">
        {isEditing ? (
          <Input
            data-testid={`session-goal-edit-input-${goal.id}`}
            value={editContent}
            onChange={(e) => setEditContent(e.target.value)}
            onBlur={handleEditBlur}
            onKeyDown={handleEditKeyDown}
            className="h-8 text-sm"
            autoFocus
            disabled={isDisabled}
          />
        ) : (
          <span
            data-testid={`session-goal-content-${goal.id}`}
            className={cn(
              'line-clamp-2 text-sm text-foreground',
              !isDisabled && 'cursor-pointer hover:underline',
            )}
            onClick={isDisabled ? undefined : handleEditClick}
          >
            {goal.content}
          </span>
        )}
      </div>

      <div className="flex items-center gap-1">
        <TooltipProvider delay={200}>
          <Tooltip>
            <TooltipTrigger
              render={
                <button
                  type="button"
                  data-testid={`session-goal-archive-btn-${goal.id}`}
                  aria-label="Archive goal"
                  className={cn(
                    buttonVariants({ variant: 'ghost', size: 'icon-sm' }),
                  )}
                  onClick={onArchive}
                  disabled={isDisabled || isEditing}
                />
              }
            >
              <LuX className="size-3.5" />
            </TooltipTrigger>
            <TooltipContent>Archive goal</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    </div>
  );
};
